import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { IconBack } from '../components/Icon';
import { setHidden, useAllMedia, useMediaPolling } from '../lib/media';
import type { MediaItem } from '../lib/media';
import { useSession } from '../lib/session';
import { useOnline } from '../lib/online';

/**
 * Artboards 1n (before fullscreen) and 1m (running) — the reception slideshow.
 * These are one page in two states, not two pages: the setup panel is the only
 * chrome that exists, and once Start is pressed there is nothing else.
 *
 * The uncuttable one (D29). Nobody will be watching it to fix problems, so the
 * rules here are about what it must never do:
 *   · never put an error on the wall — a failed refresh keeps cycling what is
 *     already in memory, silently;
 *   · never show a blank screen — an empty manifest gets a holding card;
 *   · never autoplay a video, because videos are not in it at all (D26).
 */

const SLIDE_MS = 9000;
const NO_REPEAT_WINDOW = 0.6;

/**
 * How much of the deck stays mounted around the photo on screen.
 *
 * **A mounted slide downloads immediately.** `.kiosk-slide` is `opacity: 0`
 * (`app.css:940`), not `display: none`, so the browser fetches and decodes
 * every slide it is given whether or not the room can see it. This screen used
 * to render `order.map(...)` — the entire deck — and `useAllMedia` deliberately
 * loads the whole gallery first, so a six-hundred-photo evening asked the
 * resort link for six hundred 2560px display copies at once, and asked the
 * laptop to hold six hundred decoded bitmaps. The deck also grows on every
 * 15 s poll, so it got worse all night. Nothing on screen showed it: the
 * current slide is the one that was requested first, so the show starts
 * normally and degrades as the queue behind it fills.
 *
 * **Eleven, not four.** This runs on one laptop on mains power, so the budget
 * here is far larger than a guest's phone would allow: eleven ~1 MB display
 * copies is ~11 MB in flight, which that machine does not notice. What matters
 * is that it stays *bounded* — the failure being fixed was an unbounded deck
 * that grew on every poll, not the number itself.
 *
 * `AHEAD` buys lead time. At the 260 kB/s §8 models for the resort a ~800 kB
 * display copy takes ~3 s, and five slides at 9 s each is 45 s of runway, so a
 * photo has long since arrived by the time the room sees it. It is also most of
 * the offline story until the bounded pre-cache lands: when the link dies the
 * show keeps going through what it already holds.
 *
 * `BEHIND` is deliberately as generous. Going backwards is not a thing this
 * screen does, but the deck is reshuffled and re-indexed at every lap boundary,
 * and slides that stay mounted across that are slides that do not have to be
 * fetched again.
 */
const BEHIND = 5;
const AHEAD = 5;

/**
 * The indices of the deck that should be mounted, wrapping at both ends.
 *
 * **Returned in fetch order, not in deck order, and that is load-bearing.** The
 * browser requests images roughly in the order they are inserted, so a
 * positional window put the five `BEHIND` slides *before* the one the room is
 * waiting for: at index 0 that is `[595…599, 0, 1…5]`, five megabytes of photos
 * that will not be seen this lap queued ahead of the photograph that gates the
 * first frame. On the 260 kB/s link §8 models for the resort that is the
 * difference between about three seconds of dark wall and about twenty.
 *
 * So: the current slide first, then forwards, then the ones behind — which is
 * also the order they are needed in. `BEHIND` buys nothing at index 0; it only
 * pays at a lap boundary, where it keeps already-fetched slides mounted.
 *
 * Exported for the test: it is pure, and the alternative is asserting on
 * opacity through jsdom, which does not run transitions.
 */
export function windowIndices(
  index: number, length: number, behind = BEHIND, ahead = AHEAD,
): number[] {
  if (length <= 0) return [];
  const at = (offset: number) => ((index + offset) % length + length) % length;

  // Fewer photos than the window is the normal case for the first hour of the
  // evening. Mount them all, still current-first, without repeating an index.
  const span = behind + ahead + 1;
  const reach = length <= span ? length - 1 : ahead;

  const out = [at(0)];
  for (let offset = 1; offset <= reach; offset += 1) out.push(at(offset));
  if (length <= span) return out;
  for (let offset = 1; offset <= behind; offset += 1) out.push(at(-offset));
  return out;
}

function shuffle<T>(input: T[]): T[] {
  const arr = [...input];
  for (let i = arr.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

export default function Display() {
  const navigate = useNavigate();
  // The whole gallery, not the first page. The deck is a shuffle of everything
  // that has been uploaded, so a partial pool is not a shorter show — it is
  // photographs that never go up on the screen at all, and nobody in the room
  // can tell that apart from the shuffle not having reached theirs yet.
  const media = useAllMedia();
  // The projector's whole job is showing what just happened, so it looks more
  // often than the gallery does. D29: this screen must never stop.
  useMediaPolling(15_000);
  const session = useSession();
  const online = useOnline();
  const [started, setStarted] = useState(false);
  const [index, setIndex] = useState(0);
  const [wakeHint, setWakeHint] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  // Photos only, and never a hidden one — the room sees what the room sees.
  const pool = useMemo(
    () => media.filter((it) => it.kind === 'photo' && !it.hidden && it.state === 'done'),
    [media],
  );

  const [order, setOrder] = useState<MediaItem[]>([]);

  useEffect(() => {
    setOrder((prev) => {
      // Keep the deck we are already playing; fold new arrivals in rather than
      // reshuffling under the room's feet.
      const fresh = new Map(pool.map((it) => [it.id, it]));
      const known = new Set(prev.map((it) => it.id));
      const added = pool.filter((it) => !known.has(it.id));

      /**
       * **Take the poll's row rather than the one already in the deck.** What
       * this effect preserves is a photo's *position*, not its object.
       *
       * `src` is a presigned S3 GET (D49), good for six hours from the start of
       * its signing window (`services/media/src/signing.ts`). Keeping `prev`'s
       * objects froze each row's URL at the moment it first entered the deck,
       * which was harmless only because the whole deck was mounted: every image
       * was fetched once, minutes after the kiosk was opened, and never
       * requested again. With a bounded window a photo is re-fetched every time
       * it re-enters — so a frozen URL becomes a 403, and an `<img>` that
       * 403s renders nothing at all. Black frame, caption band still up,
       * nothing in any log. Six hours is an evening: it would have started
       * mid-reception.
       *
       * This refreshes what a poll returned, which is the head page. A deck
       * deeper than that still holds older URLs — see the bounded pre-cache in
       * §8 of the todo, which is where re-signing the tail belongs.
       */
      const kept = prev.filter((it) => fresh.has(it.id)).map((it) => fresh.get(it.id)!);
      if (kept.length === 0) return shuffle(pool);
      return added.length ? [...kept, ...shuffle(added)] : kept;
    });
  }, [pool]);

  const advance = useCallback(() => {
    setIndex((i) => {
      if (order.length === 0) return 0;
      const next = i + 1;
      if (next < order.length) return next;
      // Reshuffle at the end, but keep the tail out of the front so the room
      // does not see the same photo twice in a row.
      const recent = new Set(order.slice(Math.floor(order.length * NO_REPEAT_WINDOW)).map((it) => it.id));
      setOrder((prevOrder) => {
        const reshuffled = shuffle(prevOrder);
        const head = reshuffled.filter((it) => !recent.has(it.id));
        const tail = reshuffled.filter((it) => recent.has(it.id));
        return [...head, ...tail];
      });
      return 0;
    });
  }, [order]);

  useEffect(() => {
    if (!started || order.length === 0) return;
    const id = setInterval(advance, SLIDE_MS);
    return () => clearInterval(id);
  }, [started, advance, order.length]);

  // Screen Wake Lock, requested once. If it is refused the operator is told
  // before fullscreen, and never after.
  useEffect(() => {
    let lock: WakeLockSentinel | null = null;
    const nav = navigator as Navigator & { wakeLock?: { request(type: 'screen'): Promise<WakeLockSentinel> } };
    nav.wakeLock?.request('screen').then((l) => { lock = l; }).catch(() => setWakeHint(true));
    if (!nav.wakeLock) setWakeHint(true);
    return () => { void lock?.release(); };
  }, []);

  const current = order[index];

  /**
   * **What the room is looking at, and it is not necessarily `order[index]`.**
   *
   * The index moves on a timer; a photograph appears when its bytes arrive.
   * Tying the two together is what put black frames on the wall: a slide
   * mounted and given `.on` in the same commit has no previous computed opacity
   * to transition from, so it does not fade in — it snaps, and if the image has
   * not arrived it snaps to nothing while the caption band sits over it. That
   * happened at every lap boundary, where the reshuffle hands the window a
   * photo it has never held.
   *
   * So the fade is driven by the image, not by the clock. `shown` only moves to
   * a slide once that slide's bytes have loaded, which means the previous photo
   * stays up until the next one is genuinely ready. Off the back of that, going
   * offline degrades into holding the last loaded photograph rather than
   * cutting to black.
   *
   * `outgoing` is the previous `shown`, set in the same update, so both are
   * mounted on the commit that swaps `.on` between them — which is the whole
   * requirement for a CSS crossfade.
   */
  const [loaded, setLoaded] = useState<ReadonlySet<string>>(new Set());
  const [shown, setShown] = useState<MediaItem | null>(null);
  const [outgoing, setOutgoing] = useState<MediaItem | null>(null);

  // Keyed on the URL rather than the photo id, because a poll re-signs `src`
  // (D49) and a re-signed URL has not been fetched yet. Keyed on the id, a
  // refreshed row would be called loaded and shown before its bytes existed.
  //
  // A consequence worth knowing rather than rediscovering: D49 pins signatures
  // to a one-hour window, so on each hour boundary every mounted row gets a new
  // URL at once, all eleven drop out of this set, and all eleven re-download.
  // Self-healing, and the failure it degrades to is holding the current photo
  // for one extra slide — which is the right way round.
  const markLoaded = useCallback((src: string) => {
    setLoaded((prev) => (prev.has(src) ? prev : new Set(prev).add(src)));
  }, []);

  useEffect(() => {
    if (!current || !loaded.has(current.src)) return;
    if (shown?.id === current.id) return;
    setOutgoing(shown);
    setShown(current);
  }, [current, loaded, shown]);

  /**
   * **A photo that leaves the deck leaves the wall, immediately.**
   *
   * `shown` is otherwise only moved by the effect above, which waits for the
   * next slide's bytes — and `mounted` pins `shown` precisely so the deck
   * cannot unmount it. Together those meant that hiding the photo on screen
   * (D40's only moderation control, and D23 leaves no other) removed it from
   * the deck while it carried on being projected, until some other slide
   * happened to load. Five ahead are usually preloaded, so usually that is
   * instant — but the case where it is not is the degraded link this screen now
   * advertises holding through, and the photo it would hold on is the one the
   * organiser just removed while the toast said "Hidden."
   *
   * Clearing it is enough: with the deck already reindexed, the effect above
   * puts the next loaded photo up on the following commit, and if none is
   * ready the holding card covers the gap rather than the hidden photograph.
   */
  useEffect(() => {
    const live = new Set(order.map((it) => it.id));
    if (shown && !live.has(shown.id)) setShown(null);
    if (outgoing && !live.has(outgoing.id)) setOutgoing(null);
  }, [order, shown, outgoing]);

  const mounted = useMemo(() => {
    const picked = windowIndices(index, order.length).map((i) => order[i]!);
    const byId = new Set(picked.map((it) => it.id));
    // `shown` and `outgoing` are pinned whether or not the window still holds
    // them: the deck can be reshuffled out from under both, and unmounting
    // either is the cut this whole arrangement exists to avoid. First in the
    // list so they sit *under* the incoming slide, which is the stacking a full
    // render used to give for free.
    const pinned = [outgoing, shown].filter(
      (it): it is MediaItem => !!it && !byId.has(it.id),
    );
    return pinned.length ? [...pinned, ...picked] : picked;
  }, [order, index, shown, outgoing]);

  /**
   * Forget the load state of anything no longer mounted, so this cannot grow
   * into a record of every photograph of the week. Returning `prev` unchanged
   * when nothing was dropped is what stops it re-rendering in a loop.
   */
  useEffect(() => {
    const live = new Set(mounted.map((it) => it.src));
    setLoaded((prev) => {
      const kept = [...prev].filter((src) => live.has(src));
      return kept.length === prev.size ? prev : new Set(kept);
    });
  }, [mounted]);

  /**
   * Back to the setup panel.
   *
   * There was no way out at all: Start went fullscreen with `cursor: none` and
   * nothing on screen to click, and pressing Escape dropped the browser out of
   * fullscreen while the app carried on believing it was still running — a
   * windowed slideshow with an invisible cursor and no exit.
   */
  const exit = useCallback(async () => {
    try {
      if (document.fullscreenElement) await document.exitFullscreen();
    } catch {
      /* Already out, or never in. Either way `started` is what matters. */
    }
    setStarted(false);
  }, []);

  // The hide hotkey sits exactly where a problem becomes visible — with the
  // person running the screen. It needs an admin session in this browser; the
  // display URL alone cannot change what the room sees.
  useEffect(() => {
    if (!started) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { void exit(); return; }
      if (e.key.toLowerCase() !== 'h' || !shown) return;
      if (!session?.isAdmin) {
        setToast('Sign in as an organiser on this laptop to hide anything.');
        setTimeout(() => setToast(null), 4000);
        return;
      }
      setHidden(shown.id, true);
      setToast('Hidden. It can be put back from the gallery.');
      setTimeout(() => setToast(null), 3000);
      advance();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [started, shown, session, advance, exit]);

  // Escape is what anyone will press first, and the browser eats it to leave
  // fullscreen without telling the app. This is the other half of that.
  useEffect(() => {
    const onChange = () => { if (!document.fullscreenElement) setStarted(false); };
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  async function start() {
    try {
      await rootRef.current?.requestFullscreen?.();
    } catch {
      /* Fullscreen is a nicety. If the browser refuses it, the show still runs. */
    }
    setStarted(true);
  }

  return (
    <div className="kiosk" ref={rootRef} style={{ cursor: started ? 'none' : 'auto' }}>
      {/* The slides are rendered whenever there is a deck, even before any of
          them can be shown — they have to be in the DOM to be downloading. What
          covers the gap is the card below, not the absence of these. */}
      {mounted.map((item) => (
        <div
          key={item.id}
          className={`kiosk-slide${item.id === shown?.id ? ' on' : ''}`}
          aria-hidden={item.id !== shown?.id}
        >
          {/* Blurred backdrop, then the photo whole on top of it. A portrait
              shot is never cropped to fit a landscape screen. Both <img> take
              the same URL, so this is one fetch per slide, not two. */}
          <img className="fill" src={item.src} alt="" aria-hidden="true" />
          {/* `onLoad` on the photo itself, not the backdrop: they share a URL
              so the browser fetches once, and this is the element whose
              arrival the room is actually waiting on. */}
          <img className="shot" src={item.src} alt="" onLoad={() => markLoaded(item.src)} />
        </div>
      ))}

      {/**
        * **Gated on `!shown`, not on an empty deck.** §7 of the frontend
        * requirements is explicit that a blank screen at the reception is the
        * one unacceptable failure, and asks for a holding card rather than a
        * void — and once the fade waits for bytes there are two ways to have
        * nothing to show, not one. A deck that has not loaded its first photo
        * yet used to fall through this branch and leave the room looking at
        * `.kiosk`'s near-black plus a monogram, which is exactly the void.
        *
        * It sits over the slides rather than replacing them (`.kiosk-hold` is
        * `position: absolute; inset: 0`), because replacing them would unmount
        * the very images whose arrival clears it.
        */}
      {!shown && (
        <div className="kiosk-hold">
          <div>
            <div className="brand">M&amp;V</div>
            <p style={{ color: '#dcd3c4', fontSize: 18, marginTop: 12 }}>
              {order.length === 0
                ? 'Nothing has been uploaded yet. The first photo of the night lands here.'
                : 'Warming up — the first photo is on its way.'}
            </p>
          </div>
        </div>
      )}

      <div className="kiosk-mono">M&amp;V</div>

      {/* D38: translucent grey band, white centred sans-serif — and it fades
          away entirely when a photo has no caption rather than sitting empty. */}
      <div className={`kiosk-band${shown?.caption ? ' on' : ''}`}>
        <div className="cap">{shown?.caption}</div>
        <div className="by">{shown?.uploaderName}</div>
      </div>

      {toast && <div className="kiosk-toast">{toast}</div>}

      {!started && (
        <div className="kiosk-setup">
          <div className="kiosk-preview">
            {(shown ?? current) ? <img src={(shown ?? current)!.src} alt="" /> : (
              /* Nothing to preview yet. Say so — a generated abstract here
                 made the screen look loaded when the pool was empty, which is
                 the one thing the operator needs to know before starting. */
              <div className="kiosk-preview-hold">
                <div className="brand">M&amp;V</div>
                <p>No photos yet. The first upload of the night appears here.</p>
              </div>
            )}
            {(shown ?? current)?.caption && (
              <div className="kiosk-band on" style={{ padding: '22px 34px' }}>
                <div className="cap" style={{ fontSize: 24 }}>{(shown ?? current)!.caption}</div>
              </div>
            )}
          </div>
          <div className="kiosk-bar">
            <button
              type="button" className="kiosk-back" onClick={() => navigate('/now')}
              aria-label="Back to the app"
            >
              <IconBack size={16} /> Back
            </button>
            <div className="brand">M&amp;V</div>
            <div className="facts">
              <span>{pool.length} {pool.length === 1 ? 'photo' : 'photos'} ready</span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                <span className={`kiosk-dot${online ? '' : ' off'}`} />
                {/* This used to promise "it will keep playing what it has",
                    which was true only by accident: the whole deck was mounted,
                    so every photo was already in the browser. A bounded window
                    holds five slides of lead instead, so the honest version is
                    that it keeps going for a while and then holds. The
                    bounded pre-cache in §8 of the todo is what would earn the
                    old sentence back. */}
                {online ? 'Connected' : 'Offline — a few more slides, then it holds on the last one'}
              </span>
              <span>Press <strong>H</strong> to hide whatever is on screen</span>
              {/* Escape is the only way out once this starts and there is
                  deliberately nothing on screen to click, so the one place it
                  can be said is here, before it starts. */}
              <span>Press <strong>Esc</strong> to come back here</span>
              {!session?.isAdmin && <span style={{ color: '#f6a06b' }}>Sign in as an organiser first to use it</span>}
              {wakeHint && <span style={{ color: '#f6a06b' }}>This browser won't hold the screen awake — turn off sleep in system settings</span>}
            </div>
            <button type="button" className="btn btn-primary" style={{ minHeight: 48, paddingInline: 26, fontSize: 16 }} onClick={start}>
              Start the slideshow
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
